/**
 * @module SAPO.Project.Video
 *
 *
 * VideoAbstract is meant to be a generic solution for video instancing in a browser.
 * In the context of the Video Project, it is being used by both the VideoHighEnd and VideoNative players.
 * Internally it can make use of several implementations and switch between them automatically based on the browser
 * and video mime type support.
 *
 * VideoHighEnd \                 / VideoHtml5Impl
 *               > VideoAbstract <
 * VideoNative  /                 \ VideoFlashImpl
 *
 *
 *
 * Supported events (* for custom ones):
 *
 * click*                    (clicking on the video implementation)
 * durationchange, dur
 * ended
 * error*, msg               (instead of throwing exceptions, errors are sent via on events)
 * load*, srcUrl, mimeType   (when video is requested for load)
 * loadedmetadata*, dims[2]  (once video metadata is available, sending video original dimensions)
 * pause
 * play
 * progress*, cache          (sends object with start, end and length, in seconds by default)
 * ready*, impl              (when implementation is ready)
 * timeupdate, t
 * volumechange, v
 *
 * @class VideoAbstract
 */

(function(window, undefined) {

    'use strict';


    SAPO.namespace('Project.Video.Controllers');

    var Cntrl = SAPO.Project.Video.Controllers,
        Event = SAPO.Dom.Event,
        Feat  = SAPO.Project.Video.Features;

    var videoImpls = {}; // name -> class
    SAPO.Project.Video.Controllers.videoImpls = videoImpls;

    var videoImplNames = []; // will be populated as abstract ctor

    var isAndroidAndNotChrome = false;
    if (Feat) {
        isAndroidAndNotChrome = (Feat.isAndroid && !Feat.isChromeForAndroid);
    }


    // for old IEs to play...
    var arrIndexOf = SAPO.Utility.arrIndexOf;



    /*var delFromArr = function(v, arr) {
        var idx = arrIndexOf(arr, v);
        if (idx !== -1) {
            arr.splice(idx, 1);
        }
    };*/

    var html5ErrorCodes = {
        1: 'MEDIA_ERR_ABORTED',
        2: 'MEDIA_ERR_NETWORK',
        3: 'MEDIA_ERR_DECODE',
        4: 'MEDIA_ERR_SRC_NOT_SUPPORTED'
    };

    var isIE8OrLess = (SAPO.Browser.IE && parseInt(SAPO.Browser.version, 10) < 9);

    var Poster = function(ctn, url) {
        //console.log('ctor(', ctn, url, ')');
        if (!ctn) { throw 'Poster.Ctor() must be invoked with a container element!'; }
        this._ctn = ctn;
        if (url) { this.set(url); }
    };

    Poster.prototype = {
        create: function() {
            //console.log('create()');
            if (this._el) { return; }
            var el = document.createElement('div');
            el.className = 'native-poster fill';

            var s = el.style;

            // should be in fill but may be overridden by page CSS :P
            s.position = 'absolute';
            s.left = 0;
            s.top  = 0;
            s.width  = '100%';
            s.height = '100%';

            s.backgroundColor = '#000';
            s.margin  = 0;
            //s.padding = 0;
            //s.cursor = 'pointer';
            if (!isIE8OrLess) {
                s.backgroundSize     = 'contain'; /* IE9+ */
                s.backgroundRepeat   = 'no-repeat';
                s.backgroundPosition = '50% 50%';
            }
            this._ctn.appendChild(el);
            this._el = el;
        },

        set: function(url) {
            //console.log('set(', url, ')');
            if (!url) { throw 'Poster.set() must be called with an URL!'; }
            this.create();
            if (isIE8OrLess) {
                this._el.style.filter = "progid:DXImageTransform.Microsoft.AlphaImageLoader(src='" + url + "', sizingMethod='scale')";
            }
            else {
                this._el.style.backgroundImage = 'url("' + url + '")';
            }
            this._url = url;
        },

        get: function() {
            //console.log('get() -> ', this._url);
            return this._url;
        },

        clear: function() {
            //console.log('clear()');
            if (!this._el) { return; }
            this._ctn.removeChild(this._el);
            delete this._el;
            delete this._url;
        },

        update: function(url) {
            //console.log('update(', url, ')');
            if (url) { this.set(url); }
            else {     this.clear();  }
        }
    };



    /**
     * @constructor VideoAbstract
     * @param {Object}             o
     * @param {String}             o.video                      video representation
     * @param {DOMElement|String}  o.container                  container element. video will occupy it entirely
     * @param {String}            [o.poster]                    if sent, is used prior to video load to represent the video
     * @param {Boolean}           [o.autoplay]                  if true, playback starts automatically. can be ignored in some mobile browsers
     * @param {Number}            [o.startTime]                 playback start time, in seconds
     * @param {Function}          [o.decomposeVideoIntoSources] if this function is set, player can switch implementations automatically and can also call error events if video can't be played at all
     */
    var VideoAbstract = function(o) {
        this._handlers = {};
        this._options = o;

        var va = this;

        this._lastVolume = ('volume' in o) ? o.volume : 1;
        this._isMuted    = ('mute'   in o) ? o.mute   : false;


        // fill videoImplNames now that all (supported) classes ought to be available
        (function() {
            for (var k in videoImpls) {
                if (!videoImpls.hasOwnProperty(k)) { continue; }
                videoImplNames.push(k);
            }
        })();

        // order can be manipulated from inclusion script (advanced case)
        (function() {
            var win = (SAPO.Project.Video.Features.isOuter() ? window : window.parent);
            var vio = win.SAPO._videoImplementations;
            if (vio) {
                //console.log('videoImplOrder override: ' + vio.join(' '));
                //console.log('videoImplNames before: ' + videoImplNames.join(' '));

                // remove from the order implementations which are unavailable (probably because browser doesn't support them, ie flash)
                var wantedFirstImpl = vio[0];
                var idx = arrIndexOf(videoImplNames, wantedFirstImpl);
                if (!idx) { return; }
                // it exists, move it to the beginning of the array
                videoImplNames.splice(idx, 1);
                videoImplNames.unshift(wantedFirstImpl);
            }
        })();

        //videoImplNames = ['flash']; // THOR HAMMER HERE

        // console.log('videoImplNames used: ' + videoImplNames.join(' '));

        o.container = s$(o.container);

        this._poster = new Poster(o.container);


        // try to elect best impl if video exists now

        //console.log('o.video', o.video);

        if (o.video) {
            //console.log('? ' + o.video.type);
            this._currentImplName = this.implChangeForMimeTypeToPlay(o.video.type);
            //console.log('! ' + this._currentImplName);

            this._src  = o.video.src;
            this._type = o.video.type;
        }
        else {
            this._currentImplName = videoImplNames[0];
            //console.log('no video, using: ' + this._currentImplName);
        }


        var initialErrorHandler = function(ev) {
            va.fire('error', ev.value || 'IMPL_ERROR');
        };

        var Impl = videoImpls[ this._currentImplName ];

        var cfg = {
            container: o.container,
            prefetch:  o.prefetch,

            onReady: function(v) {
                // console.log('ABSTR READY', o, v);
                va._impl = v;

                v.removeEventListener('error', initialErrorHandler);

                va._subscribeImplEvents();
                va._subscribeVpaidImplEvents(); // VPAID 1

                if (o.onReady) {
                    o.onReady(va);
                }

                v.setVolume(o.mute ? 0 : (o.volume || 1) );

                va.fire('impl_ready', va._currentImplName); // fire the impl_ready if it's not fired before

                va.fire('ready', va._currentImplName); // used by metadata
            }
        };
        if (o.video) { cfg.src = this._src; }

        if (!Impl) {
            setTimeout(
                function() {
                    va.fire('error', 'FLASH_UNAVAILABLE');
                },
                0
            );
            return;
        }

        // console.log('NEW ' + this._currentImplName + ' IMPL IN CTOR');
        var impl = new Impl(cfg, this);

        impl.addEventListener('error', initialErrorHandler);
    };




    VideoAbstract.prototype = {

        // EVENTS

        /**
         * subscribes an abstract event
         * check supported events at the beginning of the file
         *
         * @function on
         * @param  {String}   evType
         * @param  {Function} cb
         */
        on: function(evType, cb) {
            var bag = this._handlers[evType];
            if (!bag) {
                this._handlers[evType] = [cb];
            }
            else {
                bag.push(cb);
            }
        },

        /**
         * check supported events at the beginning of the file
         *
         * @function off
         * @param  {String}   evType
         * @param  {Function} cb
         */
        off: function(evType, cb) {
            var bag = this._handlers[evType];
            if (!bag) { return; }
            for (var i = 0, f = bag.length; i < f; ++i) {
                if (bag[i] === cb) {
                    bag.splice(i, 1);
                    --i;
                    --f;
                }
            }
        },

        /**
         * fires an event. can receive additional arguments, which will get to the subscribers
         *
         * @function on
         * @param  {String}  evType
         */
        fire: function(evType) {
            //if (evType === 'error') { console.log(arguments[1]); }
            var bag = this._handlers[evType];
            if (!bag) { return; }
            for (var i = 0, f = bag.length; i < f; ++i) {
                if (!bag[i]) { continue; } // TODO SHOULD NOT HAPPEN
                bag[i].apply(null, arguments);
            }
        },



        /**
         * replaces current implementation with remaining one
         *
         * @function switchImpl
         * @param {String}   implName implementation name
         * @param {Function} [cb]     called once the switch has been made
         */
        switchToImpl: function(implName, cb) {
            //console.warn( new Error().stack );

            //console.log('NEW ' + implName + ' IN SWITCH TO IMPL CB:' + (typeof cb) );
            var that = this;
            var o = this._options;
            var video;

            if (cb && typeof cb !== 'function') {
                video = cb;
                cb = undefined;
            }

            if (cb) { o.onReady = cb;   }
            else {    delete o.onReady; }

            var Impl = videoImpls[implName];
            if (!Impl) {
                return false;
            }

            if (this.isPlaying()) {
                this.pause();
            }

            this._currentImplName = implName;
            this._impl.kill();

            var impl2 = new Impl({
                container: o.container,
                prefetch:  o.prefetch,
                video:     video || o.video, // TODO REMOVE || o.video?

                onReady: function(v) {
                    //console.log('SWITCH 2', v);

                    that._impl = v;
                    that._src  = video ? video.src  : undefined;
                    that._type = video ? video.type : undefined;

                    if (video) {
                        that.fixSrcType();
                    }

                    that._subscribeImplEvents();
                    that._subscribeVpaidImplEvents(); // VPAID 1

                    if (o.onReady) {
                        o.onReady(that);
                    }

                    if ('volume' in o) {
                        v.setVolume(o.volume);
                    }

                    that.fire('ready', that._currentImplName); // used by metadata
                }
            }, this);

            impl2.addEventListener('error', function(ev) {
                that.fire('error', ev.value);
            });

            return true;
        },


        fixSrcType: function() { // TODO THIS SHOULD BE CLEANER :P
            /*console.log('_src',  this._src);
            console.log('_type', this._type);*/

            if (this._currentImplName === 'flash' && typeof this._src === 'object') {
                this._src = this._src.rtmp;
                this._type = 'rtmp/mp4';
            }
            else if (this._currentImplName === 'html5' && typeof this._src === 'object') {
                this._src = this._src.hls;
                this._type = 'application/vnd.apple.mpegurl';
            }

            /*console.log('_src',  this._src);
            console.log('_type', this._type);*/
        },


        // LOAD AND PLAY STATE

        canAutoplay: function() {
            return this._impl ? this._impl.canAutoplay() : true;
        },

        /**
         * request the video player to load a new video
         *
         * @function load
         * @param {Object} video video source presentation (has src:string, type:StringOrArr and optionally extras:object)
         */
        load: function(video) {
            // console.warn('VA LOAD CALLED: src:' + video.src + ' | type:' + video.type);

            var that = this;

            var delay = function(){
                that.load(video);
                // Remove the listener for the delayed execution
                that.off('impl_ready', delay);
            };

            // Delay the execution until there is an implementation
            if (!this._impl || !this._impl.isReady) {
                this.on('impl_ready', delay);
                return;
            }

            try {
                var targetImpl = this.implChangeForMimeTypeToPlay(video.type);
                //console.log(video.type, targetImpl);

                if (!targetImpl) { // NO SWITCH REQUIRED...
                    // console.log('VA LOAD - NO SWITCH REQUIRED.');
                    this._type = video.type;
                    this._src  = video.src;

                    this.fixSrcType();

                    //console.log('IMPL LOAD', this._src);
                    this._impl.load(this._src, this._type, this._options.autoplay, video.extras);

                    this.fire('load', this._src, this._type);

                    if (this._options.startTime && !this.isLive()) {
                        //console.log('>> %s', this._options.startTime.toFixed(1));
                        this.setCurrentTime2(this._options.startTime);
                        delete this._options.startTime;
                    }
                }
                else {
                    // console.log('VA LOAD - SWITCH TO %s REQUIRED.', targetImpl);
                    if ( targetImpl.indexOf('vpaid') !== -1 ) { // VPAID
                        // console.log('SYNC SWITCH');
                        return this.switchToImpl(targetImpl, video); // TODO video format?
                    }
                    else {  // NOT VPAID
                        return this.switchToImpl(targetImpl, function() {
                            // console.log('ASYNC SWITCH');
                            that.load(video);
                        });
                    }
                }

            } catch (ex) {
                return this.fire('error', 'URL_OF_UNKNOWN_OR_UNSUPPORTED_MIME_TYPE');
            }

            /*if (this._options.autoplay) {
                that.clearPoster();
                //this.play();
            }*/
        },

        resize: function(dims) {
            if (this._impl && this._impl.resize) {
                this._impl.resize(dims);
            }
        },

        unload: function() {
            this._impl.unload();
        },

        /**
         * returns true iif video is playing
         *
         * @function isPlaying
         * @return {Boolean} true iif video is playing
         */
        isPlaying: function() {
            try {
                return this._impl.isPlaying();
            } catch (ex) {
                return false;
            }
        },

        /**
         * plays the video
         *
         * @function play
         */
        play: function() {
            try {
                this._impl.play();
            } catch (ex) {}
        },

        /**
         * pauses the video
         *
         * @function pause
         */
        pause: function() {
            try {
                this._impl.pause();
            } catch (ex) {}
        },

        /**
         * stop playback (pauses and returns to the beginning)
         * (convenience method)
         *
         * @function stop
         */
        stop: function() {
            this.pause();
            this.setCurrentTime(0);
        },

        /**
         * toggle playback state
         * (convenience method)
         *
         * @function togglePlay
         */
        togglePlay: function() {
            if (this.isPlaying()) { this.pause(); }
            else {                  this.play();  }
            return true;
        },



        // TIME-RELATED

        /**
         * returns current time, in seconds
         *
         * @function getCurrentTime
         * @return {Number} current time in seconds
         */
        getCurrentTime: function() {
            return this._impl.getCurrentTime();
        },

        /**
         * moves the playback to the specified time
         *
         * @function setCurrentTime
         * @param {Number}  t               time to go to, in seconds
         * @param {Boolean} [userInitiated] use true if action was generated by user interaction
         */
        setCurrentTime: function(t) {
            this._impl.setCurrentTime(t);
        },

        /**
         * returns the video duration in seconds
         *
         * @function getDuration
         * @return {Number|undefined} video duration in seconds. undefined if live video
         */
        getDuration: function() {
            if (this._impl) { // TODO not sure this if is still required but I'll keep if for now
                //try {
                    return this._impl.getDuration();
                //} catch (ex) {}
            }
        },

        /**
         * returns the video cache
         *
         * @function getCache
         * @param {Boolean} [asRatio] if true, values are in 0-1 range, otherwise 0-duration
         * @return {Object} video cache params: start, end, length
         */
        getCache: function(asRatio) {
            return this._impl.getCache(asRatio);
        },

        /**
         * returns if the video is live or not
         *
         * @function isLive
         * @return {Boolean} true iif video is live
         */
        isLive: function() {
            return this._impl.isLive();
        },

        /**
         * returns iif the implementation is ready
         *
         * @function isReady
         * @return {Boolean} true iif impl is ready
         */
        isReady: function() {
            if (!this._impl) { return false; }
            return this._impl.isReady();
        },

        /**
         * scrub the video, i.e., move in the timeline by dt seconds
         * (convenience method)
         *
         * @function scrub
         * @param {Number} dt delta time in seconds
         */
        scrub: function(dt) {
            var t = this.getCurrentTime() + dt;
            var d = this.getDuration();
            if (t < 0) { t = 0; }
            if (t > d) { t = d; }
            this.setCurrentTime(t);
        },



        // VOLUME-RELATED

        /**
         * returns the video volume, a number between 0 and 1
         *
         * @function getVolume
         * @return {Number} current video volume, between 0 and 1
         */
        getVolume: function() {
            return this._impl.getVolume();
        },

        /**
         * sets the player volume
         *
         * @function setVolume
         * @param {Number}  vol volume to set to, 0 for muted, 1 for maximum
         */
        setVolume: function(vol) {
            this._impl.setVolume(vol);
        },

        /**
         * increment volume by a delta
         * (convenience method)
         *
         * @function changeVolume
         * @param {Number} dv delta volume
         */
        changeVolume: function(dv) {
            var v = this.getVolume() + dv;
            if (v < 0) { v = 0; }
            if (v > 1) { v = 1; }
            this.setVolume(v);
        },

        /**
         * mutes the video's volume
         * (convenience method)
         *
         * @function mute
         */
        mute: function() {
            this._isMuted = true;
            this._lastVolume = this.getVolume();
            this.setVolume(0);
        },

        /**
         * unmutes the sound
         * (convenience method)
         *
         * @function unmute
         */
        unmute: function() {
            this._isMuted = false;
            this.setVolume(this._lastVolume);
        },

        /**
         * toggle sound mute state
         * (convenience method)
         *
         * @function toggleMute
         * @return {Boolean} true iif end state is muted
         */
        toggleMute: function() {
            if (this._isMuted) { this.unmute(); }
            else {               this.mute();   }
            return this._isMuted;
        },

        /**
         * returns true iif volume is muted
         * (convenience method)
         *
         * @function isMuted
         * @return {Boolean} true iif video volume is muted (0)
         */
        isMuted: function() {
            return this._isMuted;
        },



        // SIZE

        /**
         * returns the original video dimensions, in pixels
         *
         * @function getVideoSize
         * @return {Number[2]} original video size in pixels
         */
        getVideoSize: function() {
            return this._impl.getVideoSize();
        },

        /**
         * returns current size of the video container
         * (convenience method)
         *
         * @function getContainerSize
         * @return {Number[2]} current size of the video container
         */
        getContainerSize: function() {
            var el = this._impl._el;
            return [el.offsetWidth, el.offsetHeight];
        },



        // POSTER

        /**
         * returns the current poster image
         *
         * @function getPoster
         * @return {String}
         */
        getPoster: function() {
            return this._poster.get();
        },

        /**
         * sets a video poster
         *
         * @function setPoster
         * @param {String} url poster image
         */
        updatePoster: function(url) {
            //console.log('UPDATE POSTER', url);
            this._poster.update(url);

            var that = this;
            if (!that._poster._subscribed && url) {
                Event.observe(that._poster._el, 'click', function() {
                    that.fire('click');
                });
                that._poster._subscribed = true;
            }
        },

        /**
         * clears poster image
         *
         * @function clearPoster
         */
        clearPoster: function() {
            //console.log('CLEAR POSTER');
            //this._poster.clear();
            //delete this._poster._subscribed;
        },



        // DOM

        /**
         * returns the video impl element (video/object element)
         *
         * @function getElement
         * @return {DOMElement}
         */
        getElement: function() {
            return this._impl.getElement();
        },

        /**
         * TODO is this useful at this level?
         *
         * @function kill
         */
        kill: function() {
            //this._subscribeImplEvents(true);

            if (this._impl) {
                this._impl.kill();
            }

            this._poster.clear();
        },



        // OTHERS

        /**
         * returns current video src (video URI)
         *
         * @function getCurrentSrc
         * @return {String} current source URI
         */
        getCurrentSrc: function() {
            return this._src;
        },

        /**
         * returns current video mime type
         *
         * @function getCurrentMimeType
         * @return {String} [description]
         */
        getCurrentMimeType: function() {
            return this._type;
        },

        /**
         * @function getCurrentImplName
         * @return {String} returns loaded impl, if any
         */
        getCurrentImplName: function() {
            return this._currentImplName;
        },

        /**
         * returns names of available implementations for the current browser
         * (i.e., if you have flash disabled, it isn't returned, same for html5)
         *
         * @function isImplAvailable
         * @param  {String}  implName
         * @return {Boolean}
         */
        isImplAvailable: function(implName) {
            return !!videoImpls[implName];
        },

        getAvailableImpls: function() {
            return videoImplNames.slice();
        },

        /**
         * This function tries its best to assert if a mime type can be played with the given impl name
         *
         * @function canMimeTypeBePlayedByImpl
         * @param  {String|String[]} mimeTypeOrMimeTypes video mime type such as 'video/mp4'. supports array too
         * @param  {String} implName either              'html5' or 'flash'
         * @return {Boolean}                             true iif impl can play the given mime type
         */
        canMimeTypeBePlayedByImpl: function(mimeTypeOrMimeTypes, implName) {
            if (typeof mimeTypeOrMimeTypes !== 'string') { // array, if any works, send it
                for (var mimeType, i = 0, I = mimeTypeOrMimeTypes.length; i < I; ++i) {
                    mimeType = mimeTypeOrMimeTypes[i];
                    if (this.canMimeTypeBePlayedByImpl(mimeType, implName)) { return true; }
                }
                return false;
            }

            var impl = videoImpls[implName];
            //if (!impl) { return false; }
            return impl.canMimeTypeBePlayed(mimeTypeOrMimeTypes);
        },

        /**
         * @function implChangeForMimeTypeToPlay
         * @param  {String|String[]} mimeTypeOrMimeTypes video mime type such as 'video/mp4'. supports array too
         * @return {String}
         * falsy means switch is not required
         * a string such as 'flash' means switch to flash is required
         * exception thrown means no supported format
         */
        implChangeForMimeTypeToPlay: function(mimeTypeOrMimeTypes) {
            var idx, otherImpls = videoImplNames.slice();

            // current impl exists. will it suffice?
            if (this._currentImplName) {
                if (this.canMimeTypeBePlayedByImpl(mimeTypeOrMimeTypes, this._currentImplName)) {
                    return; // no change required. current impl will suffice
                }

                idx = arrIndexOf(otherImpls, this._currentImplName);
                otherImpls.splice(idx, 1);
            }

            var implName, i, I = otherImpls.length;
            for (i = 0; i < I; ++i) {
                implName = otherImpls[i];
                if (this.canMimeTypeBePlayedByImpl(mimeTypeOrMimeTypes, implName)) {
                    return implName; // impl found. switch to it must be applied
                }
            }

            throw 'This mime type cannot be played "' + mimeTypeOrMimeTypes + '"';
        },

        /**
         * Given a video representation (uri string, object...) returns a source (Object with src and type) that is expected to
         * be well played by the given implementation.
         *
         * @function checkPlaybackIsPossible
         * @param  {String|Object} o video representation
         * (uses decomposeVideoIntoSources internally to decompose it into sources)
         * @param  {String} [impl] implementation name. if ommitted, uses current one
         * @return {Object} source. Object featuring src and type. returns undefined if no viable source is found
         */
        /*checkPlaybackIsPossible: function(o, impl) {
            if (!impl) {
                impl = this._currentImplName;
            }
            var source, sources = this._options.decomposeVideoIntoSources(o);
            // console.log('checkPlaybackIsPossible...');

            for (var i = 0, f = sources.length; i < f; ++i) {
                source = sources[i];
                if (this.canMimeTypeBePlayedByImpl(source.type, impl)) {
                    // console.log('CAN     be played by ' + impl);
                    return source;
                }
            }
            // console.log('CAN NOT be played by ' + impl);
        },*/



        setCurrentTime2: function(t) {
            // console.log('setCurrentTime2 abst -> ', t);

            if (!Feat.isIOS && !SAPO.Browser.IE) {
                if (this.getCurrentImplName() === 'html5') {
                    var impl = this._impl;
                    var src = impl._el.getAttribute('src');
                    var idx = src.lastIndexOf('#');
                    if (idx !== -1) {
                        src = src.substring(0, idx);
                    }
                    src = [src, t].join('#t=');
                    impl._el.setAttribute('src', src);
                    impl._el.load();

                    if (!Feat.isAndroid) {
                        return; // ANDROID: DO BOTH APPROACHES AND PRAY
                    }
                }
            }

            var that = this;

            /*var wasPlaying = that.isPlaying();
            if (wasPlaying) { that.pause(); }*/

            var attemptsLeft = 8;
            var threshold = (this.getCurrentImplName() === 'flash') ? 0.25 : 0.125;
            var deltaTimer = 250;

            var cb = function cb() {
                --attemptsLeft;
                try {
                    that.setCurrentTime(t);
                    var ct = that.getCurrentTime();
                    if (Math.abs(ct - t) <= threshold) {
                        attemptsLeft = 0;
                    }
                    threshold *= 2;

                    // console.log('attempts: %d, ct:%.2f, dt:%.2f', attemptsLeft, ct, ct - t);
                } catch (ex) {
                    // console.log('ex:', ex);
                }
                if (attemptsLeft) {
                    // console.log('next...');
                    setTimeout(cb, deltaTimer);
                }
                else {
                    // console.log('leaving.');
                    /*if (wasPlaying) {*/ that.play(); //}
                }
            };

            cb();
        },

        _subscribeVpaidImplEvents: function() {
            var v = this._impl;
            var that = this;

            if (!('_aw' in v)) { return; }

            var log = function() {};
            //var log = function() { window.console.log.apply(window.console, arguments); }; // TODO TEMP

            var aw = v._aw;

            /*
                AdClickThru
                AdError
                AdExpandedChange
                AdImpression
                AdLinearChange
                AdLoaded
                AdLog
                    AdPaused
                    AdPlaying
                    AdRemainingTimeChange
                    AdStarted
                    AdStopped
                AdUserAcceptInvitation
                AdUserClose AdUserMinimize
                AdVideoComplete
                AdVideoFirstQuartile
                AdVideoMidpoint
                AdVideoStart
                AdVideoThirdQuartile
                AdVolumeChange
                    AdDurationChange
                AdInteraction
                AdSizeChange
                AdSkipped
                AdSkippableStateChange
            */

            aw.subscribeSane('AdImpression', function() {
                log('AdImpression');
                that.fire('impression');
            });

            aw.subscribeSane('AdStarted',             function() { log('AdStarted');             v._awState.playing = true;  v.fire('play');           }); // TODO VALIDATE
            aw.subscribeSane('AdPlaying',             function() { log('AdPlaying');             v._awState.playing = true;  v.fire('play');           });
            aw.subscribeSane('AdPaused',              function() { log('AdPaused');              v._awState.playing = false; v.fire('pause');          });
            aw.subscribeSane('AdStopped',             function() { log('AdStopped');             v._awState.ended   = true;  v.fire('ended');          });
            aw.subscribeSane('AdDurationChange',      function() { log('AdDurationChange');                                  v.fire('durationchange'); });
            aw.subscribeSane('AdRemainingTimeChange', function() { log('AdRemainingTimeChange');                             v.fire('timeupdate');     });

            aw.subscribeSane('AdVideoComplete', function() { log('AdVideoComplete'); });

            setTimeout(function() {
                // console.log('AW DURATION:', aw.getAdDuration(), v.getDuration() );
                v.fire('durationchange');
                // v.fire('loadedmetadata');
            }, 1000);
        },


        _subscribeImplEvents: function() {
            var v = this._impl;
            var that = this;

            // console.log('_subscribeImplEvents');


            // fire supported events upwards, enriched
            if (this._currentImplName === 'flash') {
                v.addEventListener('click', function(ev) {
                    that.fire('click', ev);
                });
            }

            v.addEventListener('durationchange', function() {
                that.fire('durationchange', v.getDuration());
            });

            v.addEventListener('ended', function() {
                that.fire('ended');
                //that.stop();
            });

            v.addEventListener('error', function(ev) {
                //console.log('IMPL FIRED ERROR', ev);

                var errorVal;
                if (ev && ev.target && ev.target.error) { // html5 error -> http://www.w3.org/TR/html5/embedded-content-0.html#media-data
                    errorVal = 'HTML5_' + html5ErrorCodes[ ev.target.error.code ];
                }
                else { // flash ah ahhhh
                    errorVal = ev.value;
                }

                that.fire('error', errorVal);
            });

            v.addEventListener('loadedmetadata', function() {
                that.fire('loadedmetadata', v.getVideoSize());
            });

            v.addEventListener('pause', function() {
                that.fire('pause');
            });

            v.addEventListener('play', function() {
                that.fire('play');
            });

            v.addEventListener('progress', function() {
                that.fire('progress');
            });

            v.addEventListener('timeupdate', function() {
                var t = v.getCurrentTime();
                // console.log("UP ", t);
                if (that._poster._subscribed && t >= 0 && !Feat.isIPhoneOrIPod) {
                    that.clearPoster();
                }

                that.fire('timeupdate', t);
            });

            v.addEventListener('volumechange', function() {
                that.fire('volumechange', v.getVolume());
            });



            v.addEventListener('waiting', function() { // not fired by chrome always - http://code.google.com/p/chromium/issues/detail?id=279213
                //console.log('waiting');
                that.fire('waiting');
            });



            if (this._currentImplName === 'flash') { // FLASH
                v.addEventListener('canplaythrough', function() {
                    //console.log('canplaythrough -> playing');
                    that.fire('playing');
                });
            }



            v.addEventListener('playing', function() { // CHROME FIX
                if (v._el.seeking) {
                    //console.log('playing && seeking -> waiting');
                    that.fire('waiting');
                }
                else {
                    //console.log('playing && !seeking -> playing');
                    that.fire('playing');
                }
            });

            v.addEventListener('seeked', function() { // CHROME FIX
                //console.log('seeked -> playing');
                that.fire('playing');
            });



            if (this._currentImplName === 'html5') {
                v._el.addEventListener('webkitendfullscreen', function() {
                    v.pause();
                    //that.fire('done');
                });
            }
        }
    };

    Cntrl.VideoAbstract = VideoAbstract;

})(window);
